import clsx from 'clsx'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState
} from 'react'
import { themeClass } from '~/styles/theme.css'
import {
  placeholderButton,
  placeholderButtonSelected,
  placeholderCategoryItems,
  placeholderCategoryTitle,
  placeholderDropdown,
  placeholderList
} from './PlaceholderList.css'
import type {
  GroupedPlaceholderItems,
  PlaceholderItem
} from './placeholderData'

export const PlaceholderList = forwardRef(
  (
    {
      items,
      command
    }: {
      items: GroupedPlaceholderItems[]
      command: ({
        category,
        placeholder,
        label
      }: { category: string; placeholder: string; label: string }) => void
    },
    ref
  ) => {
    const [selectedItem, setSelectedItem] = useState(0)
    const [flattened, setFlattened] = useState<PlaceholderItem[]>([])

    // Use the first item in the array
    const groupedItems = items[0] || []

    // Flatten the grouped items for keyboard navigation
    useEffect(() => {
      const flat: PlaceholderItem[] = []
      for (const category of groupedItems) {
        for (const item of category.items) {
          flat.push({
            category: category.category,
            ...item
          })
        }
      }
      setFlattened(flat)
    }, [groupedItems])

    const selectItem = useCallback(
      (item: PlaceholderItem) => {
        if (item) {
          command({
            category: item.category,
            placeholder: item.placeholder,
            label: item.labelExtended ?? item.label
          })
        }
      },
      [command]
    )

    useImperativeHandle(
      ref,
      () => ({
        onKeyDown: ({ event }: { event: KeyboardEvent }) => {
          if (event.key === 'ArrowUp') {
            setSelectedItem(
              (selectedItem + flattened.length - 1) % flattened.length
            )
            return true
          }

          if (event.key === 'ArrowDown') {
            setSelectedItem((selectedItem + 1) % flattened.length)
            return true
          }

          if (event.key === 'Enter') {
            if (flattened[selectedItem]) {
              selectItem(flattened[selectedItem])
            }
            return true
          }
        }
      }),
      [flattened, selectedItem, selectItem]
    )

    if (groupedItems.length === 0) {
      return null
    }

    return (
      <div className={clsx(themeClass, placeholderDropdown)}>
        <div className={placeholderList}>
          {groupedItems.map((category) => (
            <div key={category.category}>
              <div className={placeholderCategoryTitle}>
                {category.category}
              </div>
              <div className={placeholderCategoryItems}>
                {category.items.map((item) => {
                  // Find the index of this item in the flattened array
                  const flatIndex = flattened.findIndex(
                    (flatItem) =>
                      flatItem.category === category.category &&
                      flatItem.placeholder === item.placeholder
                  )

                  return (
                    <button
                      className={clsx(
                        placeholderButton,
                        flatIndex === selectedItem
                          ? placeholderButtonSelected
                          : null
                      )}
                      type="button"
                      key={`${category.category}-${item.placeholder}`}
                      onClick={() =>
                        selectItem({
                          ...item,
                          category: category.category
                        })
                      }
                    >
                      {item.label}
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }
)
